import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { err, ok, type Result } from '@shared/result';
import type { IDocumentStorage, UploadDocumentInput, UploadError } from '@application/ports';
import { firebase } from '../firebase/client';

/**
 * Uploading the two documents this product holds: the signed vacation registro and
 * the IMSS medical certificate.
 *
 * The bytes go straight to Storage rather than through a callable. That is not a
 * shortcut — a function would have to buffer a 10 MiB file in memory, and the
 * Storage rules already confine each person to their own prefix, which is a
 * stronger check than a function could make from a multipart body. What the client
 * cannot be trusted with is the *pointer*: `registerIncapacity` re-derives the
 * expected prefix from the caller's own claims and refuses a path outside it.
 * (Until 7 Oct 2026 a vacation request carried one too — its signed registro in
 * PDF — and `submitVacationRequest` did the same. That upload is gone.)
 *
 * The path shape is fixed by `storage.rules` and must not drift from it:
 *
 *   requests/{areaId}/{personId}/{recordId}/{fileName}
 *   incapacities/{areaId}/{personId}/{recordId}/{fileName}
 *
 * A **path** is returned rather than a download URL. Firebase download URLs are
 * bearer tokens that never expire — storing one on a request document would make
 * every medical certificate readable by anyone who ever saw the field, forever,
 * regardless of what the rules say afterwards.
 */

/**
 * The limit the design states to the person ("máx. 5 MB"), which is stricter than
 * the 10 MiB ceiling in `storage.rules`. Enforcing the number they were shown is
 * the point: a 7 MB file that the copy implied was fine would be accepted here and
 * then rejected by the rules as an opaque failure.
 */
/** Las tres carpetas del bucket. Una sola lista para que las rutas no deriven. */
type StorageRoot = 'requests' | 'incapacities' | 'expedientes';

const MAX_BYTES = 5 * 1024 * 1024;

const ALLOWED_TYPE = 'application/pdf';

/**
 * Strips everything a path segment must not contain.
 *
 * A file called `../../otro.pdf` would otherwise climb out of the person's own
 * folder, and the Storage rules match on segments — a name carrying a slash
 * silently changes which rule applies.
 */
const safeFileName = (fileName: string): string => {
  const cleaned = fileName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/^[.-]+/, '')
    .slice(-80);

  return cleaned.length > 0 ? cleaned : 'documento.pdf';
};

/**
 * The checks that must hold before any upload, in either mode.
 *
 * Shared rather than living inside the Firebase adapter, because a demo that
 * accepts a `.txt` is a demo that shows a flow the product does not have — and
 * this is the code path a reviewer actually exercises. `accept="application/pdf"`
 * on the input filters the file dialog only; drag-and-drop does not go through it.
 */
const validate = (file: Blob): UploadError | null => {
  if (file.size > MAX_BYTES) {
    return { kind: 'too-large', bytes: file.size, limit: MAX_BYTES };
  }
  if (file.type !== ALLOWED_TYPE) {
    return { kind: 'wrong-type', contentType: file.type };
  }
  return null;
};

/** The one place the path shape is built, so the two adapters cannot drift. */
const pathFor = (root: StorageRoot, input: UploadDocumentInput): string =>
  `${root}/${input.areaId}/${input.ownerPersonId}/${input.recordId}/${safeFileName(input.fileName)}`;

const describe = (cause: unknown): UploadError => {
  const raw = typeof cause === 'object' && cause !== null ? (cause as { code?: unknown }).code : '';
  const code = typeof raw === 'string' ? raw : '';

  if (code.includes('unauthorized') || code.includes('permission')) {
    return { kind: 'permission-denied' };
  }
  if (code.includes('retry-limit') || code.includes('network')) return { kind: 'network' };
  return { kind: 'unknown', detail: String(cause) };
};

export class FirebaseDocumentStorage implements IDocumentStorage {
  uploadIncapacityCertificate(input: UploadDocumentInput): Promise<Result<string, UploadError>> {
    return this.upload('incapacities', input);
  }

  uploadPersonDocument(input: UploadDocumentInput): Promise<Result<string, UploadError>> {
    return this.upload('expedientes', input);
  }

  private async upload(
    root: StorageRoot,
    input: UploadDocumentInput,
  ): Promise<Result<string, UploadError>> {
    // Checked before the request leaves the browser so the person gets a sentence
    // instead of a rules rejection that reads as a network failure.
    const invalid = validate(input.file);
    if (invalid !== null) return err(invalid);

    const path = pathFor(root, input);

    try {
      const { storage } = firebase();
      await uploadBytes(ref(storage, path), input.file, { contentType: ALLOWED_TYPE });
      return ok(path);
    } catch (cause) {
      return err(describe(cause));
    }
  }

  /** Minted on demand for one viewer, never stored on a document. */
  async resolveDownloadUrl(storagePath: string): Promise<Result<string, UploadError>> {
    try {
      const { storage } = firebase();
      return ok(await getDownloadURL(ref(storage, storagePath)));
    } catch (cause) {
      return err(describe(cause));
    }
  }
}

/**
 * Demo mode has no bucket. It returns the same path shape the real adapter would,
 * so the wizards exercise the identical code path and a reviewer sees the real
 * flow rather than a branch that only exists for them.
 */
export class DemoDocumentStorage implements IDocumentStorage {
  uploadIncapacityCertificate(input: UploadDocumentInput): Promise<Result<string, UploadError>> {
    return this.pretend('incapacities', input);
  }

  uploadPersonDocument(input: UploadDocumentInput): Promise<Result<string, UploadError>> {
    return this.pretend('expedientes', input);
  }

  /** Everything the real adapter does except the network call — validation included. */
  private pretend(
    root: StorageRoot,
    input: UploadDocumentInput,
  ): Promise<Result<string, UploadError>> {
    const invalid = validate(input.file);
    return Promise.resolve(invalid !== null ? err(invalid) : ok(pathFor(root, input)));
  }

  /**
   * Un PDF de mentira, para que el visor sea el de verdad.
   *
   * Antes devolvía un error, así que en el demo el visor de documentos sólo sabía
   * enseñar su mensaje de fallo: quien revisa la pantalla veía la pieza que nunca
   * se usa y no la que se usa siempre. El PDF se arma aquí mismo —el demo no
   * tiene bucket ni debe descargar nada— y se sirve como `blob:`, que es lo que
   * el CSP de producción ya permite en `frame-src`.
   */
  resolveDownloadUrl(storagePath: string): Promise<Result<string, UploadError>> {
    if (typeof URL.createObjectURL !== 'function') {
      return Promise.resolve(err({ kind: 'unknown', detail: 'No hay archivos en modo demo.' }));
    }
    const guardada = this.urls.get(storagePath);
    if (guardada !== undefined) return Promise.resolve(ok(guardada));

    const url = URL.createObjectURL(pdfDeEjemplo(storagePath));
    this.urls.set(storagePath, url);
    return Promise.resolve(ok(url));
  }

  /** Uno por ruta: el visor pide la misma cada vez que se vuelve a ese documento. */
  private readonly urls = new Map<string, string>();
}

/**
 * El PDF de una página que ve el demo.
 *
 * Escrito a mano y no traído de un archivo: un binario en el repositorio para
 * esto sería un archivo que nadie vuelve a mirar. La tabla `xref` lleva los
 * desplazamientos de verdad —un PDF sin ella lo rechazan varios visores— y todo
 * es ASCII, así que la posición en caracteres es la posición en bytes.
 */
const pdfDeEjemplo = (storagePath: string): Blob => {
  const tipo = storagePath.split('/').at(-2) ?? 'documento';
  const lineas = [
    'BT /F1 22 Tf 60 700 Td (Documento de ejemplo) Tj ET',
    `BT /F1 12 Tf 60 664 Td (Tipo: ${tipo.replace(/[()\\]/g, '')}) Tj ET`,
    'BT /F1 12 Tf 60 640 Td (El modo demo no tiene archivos: este PDF se genera al abrirlo.) Tj ET',
  ].join('\n');

  const objetos = [
    '<</Type/Catalog/Pages 2 0 R>>',
    '<</Type/Pages/Kids[3 0 R]/Count 1>>',
    '<</Type/Page/Parent 2 0 R/MediaBox[0 0 612 792]/Resources<</Font<</F1 5 0 R>>>>/Contents 4 0 R>>',
    `<</Length ${String(lineas.length)}>>\nstream\n${lineas}\nendstream`,
    '<</Type/Font/Subtype/Type1/BaseFont/Helvetica>>',
  ];

  let pdf = '%PDF-1.4\n';
  const posiciones: number[] = [];
  objetos.forEach((cuerpo, i) => {
    posiciones.push(pdf.length);
    pdf += `${String(i + 1)} 0 obj\n${cuerpo}\nendobj\n`;
  });

  const inicioXref = pdf.length;
  pdf += `xref\n0 ${String(objetos.length + 1)}\n0000000000 65535 f \n`;
  for (const posicion of posiciones) pdf += `${String(posicion).padStart(10, '0')} 00000 n \n`;
  pdf += `trailer\n<</Size ${String(objetos.length + 1)}/Root 1 0 R>>\nstartxref\n${String(inicioXref)}\n%%EOF\n`;

  return new Blob([pdf], { type: 'application/pdf' });
};
